import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useEffect, useState } from 'react'
import {
	redirect,
	type LoaderFunctionArgs,
	type MetaFunction, useLoaderData, useFetcher, Link
} from 'react-router'
import { StripePaymentForm } from '#app/components/stripe-payment-form.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { getUserId } from '#app/utils/auth.server.ts'
import { prisma } from '#app/utils/db.server.ts'
import { PLAN_CONFIGS } from '#app/utils/stripe.server.ts'

export async function loader({ request, params }: LoaderFunctionArgs) {
	console.log('[CHECKOUT] Starting checkout flow...')

	// Get userId if logged in, but don't require it
	const userId = await getUserId(request)
	console.log('[CHECKOUT] User ID:', userId || 'anonymous')

	const { planType } = params

	// Validate plan type
	if (!planType || !['FREE', 'PRACTITIONER', 'COUPLE'].includes(planType)) {
		console.error('[CHECKOUT] Invalid plan type:', planType)
		throw new Response('Invalid plan type', { status: 400 })
	}

	const typedPlanType = planType as keyof typeof PLAN_CONFIGS
	const planConfig = PLAN_CONFIGS[typedPlanType]

	// For FREE tier with no user, redirect to signup (no payment to collect)
	if (typedPlanType === 'FREE' && !userId) {
		console.log(
			'[CHECKOUT] FREE tier for anonymous user - redirecting to signup',
		)
		return redirect(
			`/signup?plan=FREE&redirectTo=${encodeURIComponent('/checkout/success')}`,
		)
	}

	// If user is logged in, check if they already have an active subscription
	if (userId) {
		const existingSubscription = await prisma.subscription.findUnique({
			where: { userId },
		})

		// If user already has this plan, redirect to success
		if (
			existingSubscription &&
			existingSubscription.planType === typedPlanType &&
			existingSubscription.status === 'ACTIVE'
		) {
			console.log(
				'[CHECKOUT] User already has active plan, redirecting to success',
			)
			return redirect('/checkout/success')
		}

		// For FREE tier with logged-in user, create subscription and redirect
		if (typedPlanType === 'FREE') {
			console.log('[CHECKOUT] Creating FREE subscription for logged-in user')
			await prisma.subscription.upsert({
				where: { userId },
				create: {
					userId,
					planType: 'FREE',
					status: 'ACTIVE',
					stripeCustomerId: '',
					stripePriceId: '',
					currentPeriodStart: new Date(),
					currentPeriodEnd: new Date('2099-12-31'),
				},
				update: {
					planType: 'FREE',
					status: 'ACTIVE',
					currentPeriodStart: new Date(),
					currentPeriodEnd: new Date('2099-12-31'),
					cancelAtPeriodEnd: false,
					canceledAt: null,
				},
			})
			return redirect('/checkout/success')
		}
	}

	const url = new URL(request.url)
	const useBetaPricing =
		typedPlanType === 'PRACTITIONER' && url.searchParams.get('beta') === 'true'

	// Get user email if authenticated
	let userEmail: string | null = null
	if (userId) {
		const user = await prisma.user.findUnique({
			where: { id: userId },
			select: { email: true },
		})
		userEmail = user?.email || null
	}

	return {
		planType: typedPlanType,
		planConfig,
		useBetaPricing,
		isAuthenticated: !!userId,
		userEmail,
		stripePublishableKey: ENV.STRIPE_PUBLISHABLE_KEY,
	} as const
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	if (!data) {
		return [{ title: 'Checkout | MyBillingsMethod' }]
	}
	return [
		{ title: `Checkout - ${data.planConfig.name} | MyBillingsMethod` },
		{
			name: 'description',
			content: `Complete your purchase for ${data.planConfig.name} plan`,
		},
	]
}

export default function CheckoutRoute() {
	const {
		planType,
		planConfig,
		useBetaPricing,
		isAuthenticated,
		stripePublishableKey,
	} = useLoaderData<typeof loader>()

	const fetcher = useFetcher<{
		clientSecret?: string
		paymentIntentId?: string
		amount?: number
		error?: string
	}>()

	const [guestEmail, setGuestEmail] = useState('')
	const [emailError, setEmailError] = useState('')
	const [stripePromise] = useState(() => loadStripe(stripePublishableKey))

	// Create payment intent on mount or when needed
	useEffect(() => {
		// Skip if already loading or loaded
		if (fetcher.state !== 'idle' || fetcher.data) return

		// For authenticated users, create payment intent immediately
		if (isAuthenticated) {
			const formData = new FormData()
			formData.append('planType', planType)
			formData.append('useBetaPricing', useBetaPricing.toString())
			void fetcher.submit(formData, {
				method: 'POST',
				action: '/api/create-payment-intent',
			})
		}
	}, [isAuthenticated, planType, useBetaPricing, fetcher])

	const handleGuestCheckout = (e: React.FormEvent) => {
		e.preventDefault()

		// Validate email
		if (!guestEmail || !guestEmail.includes('@')) {
			setEmailError('Please enter a valid email address')
			return
		}

		setEmailError('')

		// Create payment intent with guest email
		const formData = new FormData()
		formData.append('planType', planType)
		formData.append('guestEmail', guestEmail)
		formData.append('useBetaPricing', useBetaPricing.toString())
		void fetcher.submit(formData, {
			method: 'POST',
			action: '/api/create-payment-intent',
		})
	}

	// Display price based on beta pricing
	const displayPrice = useBetaPricing
		? ('betaDisplayPrice' in planConfig ? planConfig.betaDisplayPrice : planConfig.displayPrice)
		: planConfig.displayPrice
	const amount = useBetaPricing
		? ('betaAnnualPrice' in planConfig ? planConfig.betaAnnualPrice : planConfig.annualPrice)
		: planConfig.annualPrice

	// Show error if payment intent creation failed
	if (fetcher.data?.error) {
		return (
			<div className="bg-white">
				{/* Navigation */}
				<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
					<div className="mx-auto max-w-5xl px-6 py-4">
						<div className="flex items-center justify-between">
							<Link to="/" className="flex items-center space-x-2">
								<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
								<span className="text-lg font-semibold text-gray-900">
									MyBillingsMethod
								</span>
							</Link>
							<Link
								to="/"
								className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
							>
								<Icon name="arrow-left" className="mr-2 h-4 w-4" />
								Back to Home
							</Link>
						</div>
					</div>
				</nav>

				<div className="container mx-auto max-w-2xl px-4 py-16">
					<div className="rounded-lg border border-red-200 bg-red-50 p-6">
						<div className="flex items-start">
							<Icon name="cross-1" className="mr-3 h-6 w-6 text-red-500" />
							<div>
								<h2 className="text-lg font-semibold text-red-900">
									Payment Setup Failed
								</h2>
								<p className="mt-2 text-sm text-red-700">{fetcher.data.error}</p>
								<Button
									variant="outline"
									className="mt-4"
									onClick={() => window.location.reload()}
								>
									Try Again
								</Button>
							</div>
						</div>
					</div>
				</div>

				{/* Footer */}
				<footer className="border-t border-gray-200 bg-white py-12">
					<div className="mx-auto max-w-5xl px-6">
						<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
							<div className="flex items-center space-x-2">
								<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
								<span className="font-semibold text-gray-900">
									MyBillingsMethod
								</span>
							</div>
							<div className="flex gap-6">
								<Link to="/privacy" className="hover:text-gray-900">
									Privacy
								</Link>
								<Link to="/tos" className="hover:text-gray-900">
									Terms
								</Link>
								<Link to="/support" className="hover:text-gray-900">
									Support
								</Link>
							</div>
						</div>
						<p className="mt-6 text-center text-xs text-gray-500">
							© {new Date().getFullYear()} MyBillingsMethod. Your data is yours.
							Always local. Always private.
						</p>
					</div>
				</footer>
			</div>
		)
	}

	// Show loading state while creating payment intent
	const isLoadingPaymentIntent = fetcher.state === 'loading' || fetcher.state === 'submitting'
	const hasPaymentIntent = !!fetcher.data?.clientSecret

	return (
		<div className="bg-white">
			{/* Navigation */}
			<nav className="sticky top-0 z-40 border-b border-gray-100 bg-white/80 backdrop-blur-sm">
				<div className="mx-auto max-w-5xl px-6 py-4">
					<div className="flex items-center justify-between">
						<Link to="/" className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-6 w-6 text-purple-600" />
							<span className="text-lg font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</Link>
						<Link
							to="/"
							className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-gray-900"
						>
							<Icon name="arrow-left" className="mr-2 h-4 w-4" />
							Back to Home
						</Link>
					</div>
				</div>
			</nav>

			<div className="container mx-auto max-w-2xl px-4 py-16">
				<div className="mb-8 text-center">
					<h1 className="text-3xl font-bold text-gray-900">Complete Your Purchase</h1>
					<p className="mt-2 text-gray-600">
						You're subscribing to the {planConfig.name} plan
					</p>
				</div>

				{/* Plan Summary */}
				<div className="mb-8 rounded-lg border border-purple-200 bg-purple-50 p-6">
					<div className="flex items-start justify-between">
						<div>
							<h2 className="text-xl font-bold text-purple-900">{planConfig.name}</h2>
							<p className="mt-2 text-sm text-purple-700">{displayPrice}</p>
							{useBetaPricing && (
								<p className="mt-1 text-xs text-purple-600">
									🎉 Beta pricing - limited to first 100 users!
								</p>
							)}
						</div>
						<div className="text-right">
							<p className="text-2xl font-bold text-purple-600">{displayPrice}</p>
							<p className="text-xs text-purple-500">billed annually</p>
						</div>
					</div>
				</div>

				{/* Guest Email Collection (if not authenticated) */}
				{!isAuthenticated && !hasPaymentIntent && (
					<div className="mb-8 rounded-lg border border-gray-200 bg-white p-6">
						<h3 className="mb-4 text-lg font-semibold text-gray-900">
							Enter Your Email
						</h3>
						<form onSubmit={handleGuestCheckout} className="space-y-4">
							<div>
								<label
									htmlFor="email"
									className="block text-sm font-medium text-gray-700"
								>
									Email Address
								</label>
								<Input
									type="email"
									id="email"
									value={guestEmail}
									onChange={(e) => setGuestEmail(e.target.value)}
									placeholder="you@example.com"
									required
									className="mt-1 !bg-white !text-gray-900 !border-gray-300"
								/>
								{emailError && (
									<p className="mt-1 text-sm text-red-600">{emailError}</p>
								)}
							</div>
							<Button
								type="submit"
								disabled={isLoadingPaymentIntent}
								className="w-full bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300"
								size="lg"
							>
								{isLoadingPaymentIntent ? (
									<>
										<Icon name="update" className="mr-2 h-5 w-5 animate-spin" />
										Loading...
									</>
								) : (
									'Continue to Payment'
								)}
							</Button>
						</form>
					</div>
				)}

				{/* Payment Form (shown when we have a client secret) */}
				{hasPaymentIntent && fetcher.data?.clientSecret && (
					<div className="rounded-lg border border-gray-200 bg-white p-6">
						<Elements
							stripe={stripePromise}
							options={{
								clientSecret: fetcher.data.clientSecret,
								appearance: {
									theme: 'stripe',
									variables: {
										colorPrimary: '#9333ea', // purple-600
									},
								},
							}}
						>
							<StripePaymentForm
								clientSecret={fetcher.data.clientSecret}
								amount={fetcher.data.amount || amount}
								planName={planConfig.name}
								returnUrl={`${window.location.origin}/checkout/success`}
							/>
						</Elements>
					</div>
				)}

				{/* Loading State */}
				{isAuthenticated && isLoadingPaymentIntent && !hasPaymentIntent && (
					<div className="rounded-lg border border-gray-200 bg-white p-12 text-center">
						<Icon name="update" className="mx-auto h-12 w-12 animate-spin text-purple-600" />
						<p className="mt-4 text-gray-600">Preparing your checkout...</p>
					</div>
				)}
			</div>

			{/* Footer */}
			<footer className="border-t border-gray-200 bg-white py-12">
				<div className="mx-auto max-w-5xl px-6">
					<div className="flex flex-col items-center justify-between gap-4 text-sm text-gray-600 md:flex-row">
						<div className="flex items-center space-x-2">
							<Icon name="lock-closed" className="h-5 w-5 text-purple-600" />
							<span className="font-semibold text-gray-900">
								MyBillingsMethod
							</span>
						</div>
						<div className="flex gap-6">
							<Link to="/privacy" className="hover:text-gray-900">
								Privacy
							</Link>
							<Link to="/tos" className="hover:text-gray-900">
								Terms
							</Link>
							<Link to="/support" className="hover:text-gray-900">
								Support
							</Link>
						</div>
					</div>
					<p className="mt-6 text-center text-xs text-gray-500">
						© {new Date().getFullYear()} MyBillingsMethod. Your data is yours.
						Always local. Always private.
					</p>
				</div>
			</footer>
		</div>
	)
}
